import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useT, useLocale } from '../i18n/LocaleContext';
import { useData } from '../data/DataContext';
import { useToast } from '../contexts/ToastContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import DataTable from '../components/ui/DataTable';
import EmptyState from '../components/ui/EmptyState';
import SearchRow, { SearchInput } from '../components/ui/SearchRow';
import Select from '../components/ui/Select';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import MemberFormModal from '../components/members/MemberFormModal';
import MemberDetailModal from '../components/members/MemberDetailModal';
import { formatCurrency, formatDate } from '../utils/format';
import useDebounce from '../hooks/useDebounce';

const STATUSES = ['active', 'pending', 'inactive'];
const STATUS_TONE = { active: 'active', inactive: 'inactive', pending: 'pending' };

export default function MembersPage() {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const { members, createMember, updateMember, deleteMember, memberTotalPaid } = useData();
  const [params, setParams] = useSearchParams();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const debouncedQuery = useDebounce(query, 200);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    if (params.get('new') === '1') {
      setEditing(null);
      setFormOpen(true);
      params.delete('new');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    return members.filter((m) => {
      if (status && m.status !== status) return false;
      if (!q) return true;
      return (
        (m.code || m.id || '').toLowerCase().includes(q) ||
        `${m.firstName} ${m.lastName}`.toLowerCase().includes(q) ||
        (m.phone || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.parish || '').toLowerCase().includes(q) ||
        (m.nationalId || '').toLowerCase().includes(q.replace(/\s+/g, ''))
      );
    });
  }, [members, debouncedQuery, status]);

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('members.filter_status') },
      ...STATUSES.map((s) => ({ value: s, label: t(`members.statuses.${s}`) })),
    ],
    [t]
  );

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (m) => { setEditing(m); setFormOpen(true); };
  const openView = (m) => { setViewing(m); };
  const askDelete = (m) => { setDeleting(m); };

  const handleSubmit = async (data) => {
    if (editing) {
      updateMember(editing.id, data);
      toast.success(t('members.toast_updated', { name: `${data.firstName} ${data.lastName}` }));
    } else {
      const next = createMember(data);
      toast.success(t('members.toast_created', { name: `${next.firstName} ${next.lastName}` }));
    }
    setFormOpen(false);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    deleteMember(deleting.id);
    toast.success(t('members.toast_deleted'));
    setDeleting(null);
  };

  const columns = [
    { key: 'id', header: t('members.field_id'), cellClassName: 'mono', render: (r) => r.code || r.id },
    {
      key: 'name',
      header: t('common.full_name'),
      render: (r) => (
        <div className="member-name-cell">
          <Avatar firstName={r.firstName} lastName={r.lastName} />
          <strong>{r.firstName} {r.lastName}</strong>
        </div>
      ),
    },
    { key: 'phone', header: t('members.field_phone'), render: (r) => r.phone || '—' },
    { key: 'parish', header: t('members.field_parish'), render: (r) => r.parish || '—' },
    { key: 'joinedAt', header: t('members.field_joined_at'), render: (r) => formatDate(r.joinedAt, t) },
    { key: 'status', header: t('common.status'), render: (r) => <Badge tone={STATUS_TONE[r.status]}>{t(`members.statuses.${r.status}`)}</Badge> },
    { key: 'totalPaid', header: t('members.field_total_paid'), render: (r) => formatCurrency(memberTotalPaid(r.id), locale) },
    {
      key: 'actions',
      header: t('common.actions'),
      render: (r) => (
        <div className="action-row">
          <button type="button" className="action-btn" onClick={() => openView(r)}>{t('common.view')}</button>
          <button type="button" className="action-btn" onClick={() => openEdit(r)}>{t('common.edit')}</button>
          <button type="button" className="action-btn danger" onClick={() => askDelete(r)}>{t('common.delete')}</button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <SearchRow>
        <SearchInput value={query} onChange={setQuery} placeholder={t('members.search_placeholder')} />
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={statusOptions} className="filter-select" />
        <Button variant="ghost" onClick={() => toast.info(t('toast.not_implemented'))}>{t('common.export')}</Button>
        <Button variant="gold" onClick={openNew}>+ {t('members.new_member')}</Button>
      </SearchRow>

      <Card
        title={t('members.title')}
        subtitle={t('members.subtitle', { count: members.length })}
        padded={false}
      >
        <DataTable
          columns={columns}
          rows={filtered}
          empty={(
            <EmptyState
              title={t('members.empty_title')}
              body={t('members.empty_body')}
              action={<Button variant="gold" onClick={openNew}>+ {t('members.new_member')}</Button>}
            />
          )}
        />
      </Card>

      <MemberFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
        initial={editing}
      />

      <MemberDetailModal
        isOpen={Boolean(viewing)}
        onClose={() => setViewing(null)}
        member={viewing}
        onEdit={() => { setEditing(viewing); setViewing(null); setFormOpen(true); }}
      />

      <ConfirmDialog
        isOpen={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={t('members.delete_confirm_title')}
        body={deleting ? t('members.delete_confirm_body', { name: `${deleting.firstName} ${deleting.lastName}` }) : ''}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
